import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import './WorkflowManager.css';

const API_URL = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:8000';

// ---------------------------------------------------------------------------
// Interfaces
// ---------------------------------------------------------------------------

interface WorkflowRegel {
  regel_id: number;
  tenant_id: number;
  name: string;
  trigger_typ: string;
  bedingung: { feld: string; wert: string } | null;
  aktion_typ: string;
  aktion_params: Record<string, string>;
  aktiv: boolean;
  erstellt_am: string | null;
}

interface WorkflowLog {
  log_id: number;
  regel_id: number | null;
  trigger_typ: string | null;
  status: string | null;
  detail: string | null;
  erstellt_am: string | null;
}

interface FormState {
  name: string;
  trigger_typ: string;
  bedingung_feld: string;
  bedingung_wert: string;
  aktion_typ: string;
  aktion_titel: string;
  aktion_beschreibung: string;
  aktion_ziel_rolle: string;
  aktiv: boolean;
}

const EMPTY_FORM: FormState = {
  name: '',
  trigger_typ: 'auftrag_status_changed',
  bedingung_feld: '',
  bedingung_wert: '',
  aktion_typ: 'erstelle_warnung',
  aktion_titel: '',
  aktion_beschreibung: '',
  aktion_ziel_rolle: 'Admin',
  aktiv: true,
};

interface Props {
  authToken: string;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const WorkflowManager: React.FC<Props> = ({ authToken }) => {
  const [regeln, setRegeln] = useState<WorkflowRegel[]>([]);
  const [logs, setLogs] = useState<WorkflowLog[]>([]);
  const [triggerTypen, setTriggerTypen] = useState<string[]>([]);
  const [aktionTypen, setAktionTypen] = useState<string[]>([]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState<number | null>(null);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [formError, setFormError] = useState<string | null>(null);

  const [activeSection, setActiveSection] = useState<'regeln' | 'logs'>('regeln');
  const [deleteConfirm, setDeleteConfirm] = useState<number | null>(null);

  const headers = { Authorization: `Bearer ${authToken}` };

  // ---------------------------------------------------------------------------
  // Load
  // ---------------------------------------------------------------------------

  const loadAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [regelnRes, logsRes, typenRes] = await Promise.all([
        axios.get(`${API_URL}/api/workflows`, { headers }),
        axios.get(`${API_URL}/api/workflows/logs?limit=20`, { headers }),
        axios.get(`${API_URL}/api/workflows/trigger-typen`, { headers }),
      ]);
      setRegeln(regelnRes.data);
      setLogs(logsRes.data);
      setTriggerTypen(typenRes.data.trigger_typen || []);
      setAktionTypen(typenRes.data.aktion_typen || []);
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Fehler beim Laden der Workflows');
    } finally {
      setLoading(false);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    loadAll();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ---------------------------------------------------------------------------
  // Form helpers
  // ---------------------------------------------------------------------------

  const openCreate = () => {
    setEditId(null);
    setForm(EMPTY_FORM);
    setFormError(null);
    setShowForm(true);
  };

  const openEdit = (r: WorkflowRegel) => {
    setEditId(r.regel_id);
    setForm({
      name: r.name,
      trigger_typ: r.trigger_typ,
      bedingung_feld: r.bedingung?.feld || '',
      bedingung_wert: r.bedingung?.wert || '',
      aktion_typ: r.aktion_typ,
      aktion_titel: r.aktion_params?.titel || '',
      aktion_beschreibung: r.aktion_params?.beschreibung || '',
      aktion_ziel_rolle: r.aktion_params?.ziel_rolle || 'Admin',
      aktiv: r.aktiv,
    });
    setFormError(null);
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setEditId(null);
    setForm(EMPTY_FORM);
    setFormError(null);
  };

  const buildPayload = () => {
    const bedingung = form.bedingung_feld.trim()
      ? { feld: form.bedingung_feld.trim(), wert: form.bedingung_wert.trim() }
      : null;
    const aktion_params: Record<string, string> = {};
    if (form.aktion_titel.trim()) aktion_params.titel = form.aktion_titel.trim();
    if (form.aktion_beschreibung.trim()) aktion_params.beschreibung = form.aktion_beschreibung.trim();
    if (form.aktion_ziel_rolle.trim()) aktion_params.ziel_rolle = form.aktion_ziel_rolle.trim();

    return {
      name: form.name.trim(),
      trigger_typ: form.trigger_typ,
      bedingung,
      aktion_typ: form.aktion_typ,
      aktion_params: Object.keys(aktion_params).length > 0 ? aktion_params : null,
      aktiv: form.aktiv,
    };
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim()) {
      setFormError('Name ist erforderlich');
      return;
    }
    setSaving(true);
    setFormError(null);
    try {
      const payload = buildPayload();
      if (editId !== null) {
        await axios.put(`${API_URL}/api/workflows/${editId}`, payload, { headers });
      } else {
        await axios.post(`${API_URL}/api/workflows`, payload, { headers });
      }
      closeForm();
      await loadAll();
    } catch (e: any) {
      setFormError(e?.response?.data?.detail || 'Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Toggle aktiv
  // ---------------------------------------------------------------------------

  const toggleAktiv = async (r: WorkflowRegel) => {
    try {
      await axios.put(
        `${API_URL}/api/workflows/${r.regel_id}`,
        { aktiv: !r.aktiv },
        { headers },
      );
      await loadAll();
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Fehler beim Aktualisieren');
    }
  };

  // ---------------------------------------------------------------------------
  // Delete
  // ---------------------------------------------------------------------------

  const handleDelete = async (regelId: number) => {
    try {
      await axios.delete(`${API_URL}/api/workflows/${regelId}`, { headers });
      setDeleteConfirm(null);
      await loadAll();
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Fehler beim Löschen');
    }
  };

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  if (loading) {
    return <div className="wf-loading">Lade Workflows...</div>;
  }

  return (
    <div className="workflow-manager">
      <div className="wf-header">
        <div>
          <h2>Workflow-Automatisierung</h2>
          <p className="wf-desc">
            Automatische Aktionen bei System-Ereignissen konfigurieren.
          </p>
        </div>
        <button className="wf-btn wf-btn--primary" onClick={openCreate}>
          + Neue Regel
        </button>
      </div>

      {error && (
        <div className="wf-error">
          {error}
          <button className="wf-error__close" onClick={() => setError(null)}>×</button>
        </div>
      )}

      {/* Section Switch */}
      <div className="wf-section-tabs">
        <button
          className={`wf-section-tab ${activeSection === 'regeln' ? 'active' : ''}`}
          onClick={() => setActiveSection('regeln')}
        >
          Regeln ({regeln.length})
        </button>
        <button
          className={`wf-section-tab ${activeSection === 'logs' ? 'active' : ''}`}
          onClick={() => setActiveSection('logs')}
        >
          Protokoll
        </button>
      </div>

      {/* ------------------------------------------------------------------ */}
      {/* Regeln                                                               */}
      {/* ------------------------------------------------------------------ */}
      {activeSection === 'regeln' && (
        <div className="wf-regeln">
          {regeln.length === 0 ? (
            <div className="wf-empty">
              Noch keine Workflow-Regeln vorhanden. Klicke "Neue Regel", um zu beginnen.
            </div>
          ) : (
            <table className="wf-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Trigger</th>
                  <th>Bedingung</th>
                  <th>Aktion</th>
                  <th>Aktiv</th>
                  <th>Aktionen</th>
                </tr>
              </thead>
              <tbody>
                {regeln.map((r) => (
                  <tr key={r.regel_id} className={r.aktiv ? '' : 'wf-row--inactive'}>
                    <td className="wf-td-name">{r.name}</td>
                    <td>
                      <span className="wf-badge wf-badge--trigger">{r.trigger_typ}</span>
                    </td>
                    <td className="wf-td-bedingung">
                      {r.bedingung
                        ? `${r.bedingung.feld} = ${r.bedingung.wert}`
                        : <span className="wf-muted">—</span>}
                    </td>
                    <td>
                      <span className="wf-badge wf-badge--aktion">{r.aktion_typ}</span>
                    </td>
                    <td>
                      <button
                        className={`wf-toggle ${r.aktiv ? 'wf-toggle--on' : 'wf-toggle--off'}`}
                        onClick={() => toggleAktiv(r)}
                        title={r.aktiv ? 'Deaktivieren' : 'Aktivieren'}
                      >
                        {r.aktiv ? 'Aktiv' : 'Inaktiv'}
                      </button>
                    </td>
                    <td className="wf-td-actions">
                      <button
                        className="wf-btn wf-btn--sm"
                        onClick={() => openEdit(r)}
                      >
                        Bearbeiten
                      </button>
                      {deleteConfirm === r.regel_id ? (
                        <>
                          <button
                            className="wf-btn wf-btn--sm wf-btn--danger"
                            onClick={() => handleDelete(r.regel_id)}
                          >
                            Bestätigen
                          </button>
                          <button
                            className="wf-btn wf-btn--sm"
                            onClick={() => setDeleteConfirm(null)}
                          >
                            Abbrechen
                          </button>
                        </>
                      ) : (
                        <button
                          className="wf-btn wf-btn--sm wf-btn--outline-danger"
                          onClick={() => setDeleteConfirm(r.regel_id)}
                        >
                          Löschen
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* ------------------------------------------------------------------ */}
      {/* Logs                                                                 */}
      {/* ------------------------------------------------------------------ */}
      {activeSection === 'logs' && (
        <div className="wf-logs">
          {logs.length === 0 ? (
            <div className="wf-empty">Noch keine Workflow-Ausführungen protokolliert.</div>
          ) : (
            <table className="wf-table">
              <thead>
                <tr>
                  <th>Zeitpunkt</th>
                  <th>Regel-ID</th>
                  <th>Trigger</th>
                  <th>Status</th>
                  <th>Detail</th>
                </tr>
              </thead>
              <tbody>
                {logs.map((l) => (
                  <tr key={l.log_id}>
                    <td className="wf-td-time">
                      {l.erstellt_am
                        ? new Date(l.erstellt_am).toLocaleString('de-DE')
                        : '—'}
                    </td>
                    <td>{l.regel_id ?? '—'}</td>
                    <td>
                      <span className="wf-badge wf-badge--trigger">{l.trigger_typ ?? '—'}</span>
                    </td>
                    <td>
                      <span
                        className={`wf-badge ${l.status === 'OK' ? 'wf-badge--ok' : 'wf-badge--error'}`}
                      >
                        {l.status ?? '—'}
                      </span>
                    </td>
                    <td className="wf-td-detail">{l.detail ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* ------------------------------------------------------------------ */}
      {/* Form Modal                                                           */}
      {/* ------------------------------------------------------------------ */}
      {showForm && (
        <div className="wf-modal-overlay" onClick={closeForm}>
          <div className="wf-modal" onClick={(e) => e.stopPropagation()}>
            <div className="wf-modal__header">
              <h3>{editId !== null ? 'Regel bearbeiten' : 'Neue Workflow-Regel'}</h3>
              <button className="wf-modal__close" onClick={closeForm}>×</button>
            </div>

            <form onSubmit={handleSubmit} className="wf-form">
              {/* Name */}
              <div className="wf-field">
                <label>Name *</label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="z.B. Warnung bei Lieferung"
                  required
                />
              </div>

              {/* Trigger */}
              <div className="wf-field">
                <label>Trigger-Typ *</label>
                <select
                  value={form.trigger_typ}
                  onChange={(e) => setForm({ ...form, trigger_typ: e.target.value })}
                >
                  {triggerTypen.map((t) => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </select>
              </div>

              {/* Bedingung (optional) */}
              <div className="wf-field-group">
                <div className="wf-field-group__label">Bedingung (optional)</div>
                <div className="wf-field-row">
                  <div className="wf-field">
                    <label>Feld</label>
                    <input
                      type="text"
                      value={form.bedingung_feld}
                      onChange={(e) => setForm({ ...form, bedingung_feld: e.target.value })}
                      placeholder="z.B. neuer_status"
                    />
                  </div>
                  <div className="wf-field">
                    <label>Wert</label>
                    <input
                      type="text"
                      value={form.bedingung_wert}
                      onChange={(e) => setForm({ ...form, bedingung_wert: e.target.value })}
                      placeholder="z.B. GELIEFERT"
                    />
                  </div>
                </div>
              </div>

              {/* Aktion */}
              <div className="wf-field">
                <label>Aktions-Typ *</label>
                <select
                  value={form.aktion_typ}
                  onChange={(e) => setForm({ ...form, aktion_typ: e.target.value })}
                >
                  {aktionTypen.map((a) => (
                    <option key={a} value={a}>{a}</option>
                  ))}
                </select>
              </div>

              {/* Aktion Params */}
              {form.aktion_typ === 'erstelle_warnung' && (
                <div className="wf-field-group">
                  <div className="wf-field-group__label">Warnungs-Parameter</div>
                  <div className="wf-field">
                    <label>Titel</label>
                    <input
                      type="text"
                      value={form.aktion_titel}
                      onChange={(e) => setForm({ ...form, aktion_titel: e.target.value })}
                      placeholder="Warnungs-Titel"
                    />
                  </div>
                  <div className="wf-field">
                    <label>Beschreibung</label>
                    <input
                      type="text"
                      value={form.aktion_beschreibung}
                      onChange={(e) => setForm({ ...form, aktion_beschreibung: e.target.value })}
                      placeholder="Detailbeschreibung"
                    />
                  </div>
                  <div className="wf-field">
                    <label>Ziel-Rolle</label>
                    <select
                      value={form.aktion_ziel_rolle}
                      onChange={(e) => setForm({ ...form, aktion_ziel_rolle: e.target.value })}
                    >
                      <option value="Admin">Admin</option>
                      <option value="Geschäftsführer">Geschäftsführer</option>
                      <option value="Lagerleiter">Lagerleiter</option>
                      <option value="Einkäufer">Einkäufer</option>
                      <option value="Verkäufer">Verkäufer</option>
                      <option value="Produktionsleiter">Produktionsleiter</option>
                      <option value="Buchhaltung">Buchhaltung</option>
                    </select>
                  </div>
                </div>
              )}

              {/* Aktiv */}
              <div className="wf-field wf-field--inline">
                <label>
                  <input
                    type="checkbox"
                    checked={form.aktiv}
                    onChange={(e) => setForm({ ...form, aktiv: e.target.checked })}
                  />
                  {' '}Regel aktiv
                </label>
              </div>

              {formError && (
                <div className="wf-form-error">{formError}</div>
              )}

              <div className="wf-form-actions">
                <button type="button" className="wf-btn" onClick={closeForm}>
                  Abbrechen
                </button>
                <button type="submit" className="wf-btn wf-btn--primary" disabled={saving}>
                  {saving ? 'Speichern...' : editId !== null ? 'Speichern' : 'Erstellen'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default WorkflowManager;
